
import { useState, useEffect } from 'react';

interface FormProgressionState {
  showEventType: boolean;
  showLocation: boolean;
  showTime: boolean;
}

interface FormProgressionProps {
  festaNome: string;
  tipoEvento: string;
  endereco: string;
  cep: string;
  cidade: string;
  estado: string;
  numero: string;
  selectedSuppliers?: any;
}

// Helper function to check if only delivery products are selected
const hasOnlyDeliveryProducts = (suppliers: any) => {
  if (!suppliers || typeof suppliers !== 'object') return false;
  
  const hasDecoracao = suppliers?.decoracao && Array.isArray(suppliers.decoracao) && suppliers.decoracao.length > 0;
  const hasDescartaveis = suppliers?.descartaveis && Array.isArray(suppliers.descartaveis) && suppliers.descartaveis.length > 0;
  
  const hasServiceCategories = 
    (suppliers?.salgados && Array.isArray(suppliers.salgados) && suppliers.salgados.length > 0) ||
    (suppliers?.doces && Array.isArray(suppliers.doces) && suppliers.doces.length > 0) ||
    (suppliers?.buffet && Array.isArray(suppliers.buffet) && suppliers.buffet.length > 0);
  
  // Return true if there are delivery products and no service categories
  return (hasDecoracao || hasDescartaveis) && !hasServiceCategories;
};

export const useFormProgression = ({
  festaNome,
  tipoEvento,
  endereco,
  cep,
  cidade,
  estado,
  numero,
  selectedSuppliers
}: FormProgressionProps) => {
  const [progression, setProgression] = useState<FormProgressionState>({
    showEventType: false,
    showLocation: false,
    showTime: false
  });

  useEffect(() => {
    // Show event type after name is filled
    if (festaNome.trim()) {
      setProgression(prev => ({ ...prev, showEventType: true }));
    } else {
      setProgression({ showEventType: false, showLocation: false, showTime: false });
      return;
    }

    // Show location after event type is selected
    if (tipoEvento) {
      setProgression(prev => ({ ...prev, showLocation: true }));
    } else {
      setProgression(prev => ({ ...prev, showLocation: false, showTime: false }));
      return;
    }

    // Show time after location is complete
    const locationComplete = endereco.trim() && cep.trim() && cidade.trim() && estado.trim() && numero.trim();
    if (locationComplete) {
      // Check if only delivery products are selected
      const onlyDeliveryProducts = hasOnlyDeliveryProducts(selectedSuppliers);
      
      if (onlyDeliveryProducts) {
        // Skip step 4 (time selection) for delivery-only products
        setProgression(prev => ({ ...prev, showTime: false }));
      } else {
        // Show step 4 for service categories
        setProgression(prev => ({ ...prev, showTime: true }));
      }
    } else {
      setProgression(prev => ({ ...prev, showTime: false }));
    }
  }, [festaNome, tipoEvento, endereco, cep, cidade, estado, numero, selectedSuppliers]);

  return {
    ...progression,
    hasOnlyDeliveryProducts: hasOnlyDeliveryProducts(selectedSuppliers)
  };
};
